import { useId } from 'react';
import type { SlaHealth, SlaView } from '@veta-desk/domain';

// Indicador de SLA como píldora horizontal seccionada ("trenecito"): cada segmento es un
// tramo del tiempo de la meta y se van llenando de izquierda a derecha mientras corre el reloj.
//  verde = sano · naranja quemado = ≥ 75 % · rojo = vencido · gris = detenido.
// Horario hábil de la empresa con la zona y los festivos del país del cliente; P1 corre 24×7.

const BLOCKS = 5; // cada segmento = 20 % del tiempo de la meta

// Lo sano casi no se nota; el color fuerte se reserva para el riesgo.
const FILL: Record<SlaHealth, string> = {
  ok: '#A9D4B8',
  warning: '#C2410C',
  breached: '#B91C1C',
  paused: '#9C9890',
  met: '#A9D4B8',
  none: 'transparent'
};
const EMPTY = '#E8E5DF';
// SLA de referencia (bolsa de horas): excedido sin el rojo de incumplimiento.
const REFERENCE_BREACHED = '#C9A27A';

const HEALTH_LABEL: Record<SlaHealth, string> = {
  ok: 'En tiempo',
  warning: 'Por vencer',
  breached: 'Vencido',
  paused: 'Detenido',
  met: 'Cumplido',
  none: 'Sin SLA'
};

export const CLOCK_LABEL = { response: 'Primera respuesta', resolution: 'Resolución', estimate: 'Entrega del estimado' } as const;

function duration(ms: number): string {
  const totalMin = Math.round(Math.abs(ms) / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 24) return `${Math.floor(h / 24)} d ${h % 24} h`;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} m`;
  return `${m} m`;
}

export function slaText(sla: SlaView, compact = false): string {
  const unit = sla.calendar === 'business' && !compact ? ' hábiles' : '';
  if (compact && sla.health === 'paused') return 'Detenido';
  switch (sla.health) {
    case 'breached': return sla.reference
      ? (sla.remainingMs < 0 ? `Excedido ${duration(sla.remainingMs)}${unit}` : 'Excedido')
      : sla.remainingMs < 0 ? `Vencido hace ${duration(sla.remainingMs)}${unit}` : 'Vencido';
    case 'met': return 'Cumplido';
    case 'paused': return `Detenido · quedan ${duration(sla.remainingMs)}${unit}`;
    case 'none': return 'Sin SLA';
    default: return `Quedan ${duration(sla.remainingMs)}${unit}`;
  }
}

export function SlaMeter({ sla, size = 'sm', showText = true, compact = false }: {
  sla: SlaView | null | undefined; size?: 'sm' | 'md'; showText?: boolean; compact?: boolean;
}) {
  const uid = useId().replace(/:/g, '');
  if (!sla || sla.health === 'none') return <span className="text-xs text-muted">—</span>;

  const what = CLOCK_LABEL[sla.clock ?? 'resolution'];
  const label = `${what}${sla.reference ? ' (referencia, mejor esfuerzo)' : ''}: ${HEALTH_LABEL[sla.health]}. ${slaText(sla)}. ${Math.round(sla.pct * 100)} % del tiempo consumido.`;

  // Reloj terminado a tiempo: ya no hay nada que vigilar, basta una marca discreta.
  if (sla.health === 'met') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted" title={label} aria-label={label}>
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2.5 6.2 5 8.5l4.5-5" fill="none" stroke="#1F8A4C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Cumplido
      </span>
    );
  }

  // Solo el SLA vencido llena la píldora completa; antes, a lo más BLOCKS-1 segmentos.
  const filled = sla.health === 'breached' ? BLOCKS
    : Math.min(BLOCKS - 1, Math.max(sla.pct > 0 ? 1 : 0, Math.round(sla.pct * BLOCKS)));
  const paused = sla.health === 'paused';
  // Píldora horizontal seccionada, delgada.
  const seg = size === 'md' ? 18 : 14;
  const gap = 2;
  const h = size === 'md' ? 7 : 6;
  const width = BLOCKS * (seg + gap) - gap;
  const clipId = `sla-clip-${uid}`;
  const hatchId = `sla-hatch-${uid}`;

  return (
    <span className={compact ? 'inline-flex flex-col items-start gap-1' : 'inline-flex items-center gap-2'} title={label}>
      <svg role="img" aria-label={label} width={width} height={h} viewBox={`0 0 ${width} ${h}`} className="shrink-0">
        <defs>
          <clipPath id={clipId}><rect x={0} y={0} width={width} height={h} rx={h / 2} /></clipPath>
          {/* En pausa: lo consumido va rayado para no confundirse con "vacío". */}
          <pattern id={hatchId} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="4" height="4" fill={EMPTY} />
            <rect width="1.6" height="4" fill={FILL.paused} />
          </pattern>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          {Array.from({ length: BLOCKS }, (_, i) => (
            <rect key={i} x={i * (seg + gap)} y={0} width={seg} height={h}
              fill={i < filled ? (paused ? `url(#${hatchId})` : sla.reference && sla.health === 'breached' ? REFERENCE_BREACHED : FILL[sla.health]) : EMPTY} />
          ))}
        </g>
      </svg>
      {showText && (
        <span className={`whitespace-nowrap text-xs tabular-nums ${
          sla.health === 'breached' ? (sla.reference ? 'font-semibold text-[#8A5A2B]' : 'font-semibold text-[#B91C1C]')
            : sla.health === 'warning' ? 'font-semibold text-[#9A3412]'
              : 'text-muted'}`}>
          {sla.health === 'warning' && !compact ? 'Por vencer · ' : ''}{slaText(sla, compact)}
        </span>
      )}
    </span>
  );
}
